import React, { useEffect, useMemo, useState } from "react";

import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";

import { Outlet, useMatch, useNavigate } from "react-router-dom";

import { ProtectedComponent, useIsAuthorisedUser } from "@components/permission_control/ProtectedComponent";
import { ResaleCart } from "@components/resale_submission/SummaryCart/ResaleCart";
import { useAuthenticationContext } from "@context/authentication";
import { UserDropDownMenu } from "@pages/UserMenu/UserDropDownMenu";
import { Role } from "@roles";

import { ROUTES } from "../../routes";

import { DashboardNav } from "./DashboardNav";

import styles from "./Dashboard.module.scss";

export const Dashboard = () => {
  const [isSelectedReport, setIsSelectedReport] = useState(true);
  const navigate = useNavigate();
  const { auth } = useAuthenticationContext();

  const isReporter = useIsAuthorisedUser({
    requiredGroups: [Role.ResaleReporter],
  });

  const isRecipient = useIsAuthorisedUser({
    requiredGroups: [Role.RoyaltyRecipient],
  });

  const isMSOrVA = useIsAuthorisedUser({
    requiredGroups: [Role.MemberServices, Role.VisualArtistAdmin],
  });

  const isData = useIsAuthorisedUser({
    requiredGroups: [Role.Data],
  });

  const isOnDashboard = useMatch(ROUTES.Dashboard.path);

  const isOnAccount = useMatch(ROUTES.Dashboard.Accounts);

  const isOnProfile = useMatch(ROUTES.Dashboard.ProfileWildcard);

  const displayCart = useMemo(() => !isOnAccount && !isOnProfile, [isOnAccount, isOnProfile]);

  useEffect(() => {
    if (!isOnDashboard) {
      return;
    }
    if (isReporter && isRecipient) {
      navigate(isSelectedReport ? ROUTES.Dashboard.ReportNewResale.path : ROUTES.Dashboard.UpcomingRoyalties.path, {
        replace: true,
      });
      // isMSOrVA need to be removed once the new dashboard for MS and VA is implemented
    } else if (isReporter || isMSOrVA) {
      navigate(ROUTES.Dashboard.ReportNewResale.path, { replace: true });
    } else if (isRecipient) {
      navigate(ROUTES.Dashboard.UpcomingRoyalties.path, { replace: true });
    } else if (isData) {
      navigate(ROUTES.Dashboard.RecentReports.path, { replace: true });
    }
  }, [isSelectedReport, isReporter, isRecipient, isOnDashboard, isMSOrVA, isData, navigate]);

  const handleToggle = (selected: boolean) => {
    setIsSelectedReport(selected);
  };

  return (
    <Container fluid>
      <div className={styles["dashboard-tools"]}>
        {displayCart && <ResaleCart></ResaleCart>}
        <div className={styles["user-menu"]}>
          <ProtectedComponent auth={auth}>
            <UserDropDownMenu />
          </ProtectedComponent>
        </div>
      </div>

      <Row className="min-vh-100">
        <Col className="nav" xs={12} sm={4} xl={3} xxl={2}>
          <DashboardNav
            isSelectedReport={isSelectedReport}
            setIsSelectedReport={handleToggle}
            isReporter={isReporter}
            isRecipient={isRecipient}
          />
        </Col>
        <Col>
          <Outlet />
        </Col>
      </Row>
    </Container>
  );
};
